import { InjectionToken } from '@angular/core';
import { LocalCache } from '@nk/caching-lib';
import {
  CustomerPersonCompositeBatchEntity,
  CustomerPersonEntity,
  CustomerPersonForm,
  EntityMonitoringReasonEntity,
  EntityMonitoringTypeEntity,
  ScreeningActionCompositeEntity,
  ScreeningActionEntity,
  ScreeningActionPatchPayload,
  ScreeningActionPostPayload,
  ScreeningAdverseMediaEntity,
  ScreeningAuditLogCompositeEntity,
  ScreeningAuditLogReportMetaCompositeEntity,
  ScreeningAuditLogReportMetaPartialEntity,
  ScreeningConfigCompositeChanges,
  ScreeningConfigCompositeEntity,
  ScreeningConfigEntity,
  ScreeningConfigPartialCompositeEntity,
  ScreeningConfigPartialEntity,
  ScreeningConfigScreeningListCategoryEntity,
  ScreeningConfigScreeningListCategoryPartialEntity,
  ScreeningConfigStatsAggregateEntity,
  ScreeningConfigSystemSuggestionSettingEntity,
  ScreeningConfigWatchlistEntity,
  ScreeningConfigWatchlistPartialEntity,
  ScreeningEntityEntity,
  ScreeningEntityPostPayload,
  ScreeningListCategoryEntity,
  ScreeningNationalIdentificationSchemaEntity,
  SimpleCollection,
  WithUuid,
} from '@nk/data-entities-lib';
import { OrderBy } from '@nk/utils-lib';
import { Observable } from 'rxjs';

export interface ScreeningConfigApiService {
  entityMonitoringTypeListCache: LocalCache<
    ScreeningConfigApiService,
    () => Observable<WithUuid<EntityMonitoringTypeEntity>[]>
  >;
  screeningActionCompositeCollectionCache: LocalCache<
    ScreeningConfigApiService,
    (
      screeningConfigUuid: string,
      monitoringOriginEntityUuid: string | undefined,
      screeningEntityUuid: string | undefined,
      approvedFlag: boolean | undefined,
      orderBy: OrderBy[],
      limit: number,
      offset: number,
      onlyPendingFlag?: boolean,
    ) => Observable<SimpleCollection<ScreeningActionCompositeEntity>>
  >;
  screeningConfigStatsAggregateCache: LocalCache<
    ScreeningConfigApiService,
    (screeningConfigUuid: string) => Observable<ScreeningConfigStatsAggregateEntity | null>
  >;
  screeningConfigsListCache: LocalCache<
    ScreeningConfigApiService,
    (appCustomerUuid: string) => Observable<WithUuid<ScreeningConfigEntity>[]>
  >;
  screeningConfigWatchlistListCache: LocalCache<
    ScreeningConfigApiService,
    (
      companyUuid: string | undefined,
      screeningConfigUuid: string,
      watchlistUuid: string | undefined,
    ) => Observable<ScreeningConfigWatchlistEntity[]>
  >;
  screeningConfigCompositeCache: LocalCache<
    ScreeningConfigApiService,
    (screeningConfigUuid: string) => Observable<ScreeningConfigCompositeEntity>
  >;
  screeningNationalIdentificationSchemaListCache: LocalCache<
    ScreeningConfigApiService,
    () => Observable<ScreeningNationalIdentificationSchemaEntity[]>
  >;
  screeningAuditLogCompositeCollectionCache: LocalCache<
    ScreeningConfigApiService,
    (
      screeningConfigUuid: string,
      monitoringOriginEntityUuid?: string,
      screeningEntityUuid?: string,
      limit?: number,
      offset?: number,
    ) => Observable<SimpleCollection<ScreeningAuditLogCompositeEntity>>
  >;
  entityMonitoringReasonCache: LocalCache<
    ScreeningConfigApiService,
    () => Observable<WithUuid<EntityMonitoringReasonEntity>[]>
  >;
  screeningConfigSystemSuggestionSettingListCache: LocalCache<
    ScreeningConfigApiService,
    () => Observable<WithUuid<ScreeningConfigSystemSuggestionSettingEntity>[]>
  >;
  screeningListCategoryListCache: LocalCache<
    ScreeningConfigApiService,
    () => Observable<ScreeningListCategoryEntity[]>
  >;
  screeningAuditLogReportMetaCompositeListCache: LocalCache<
    ScreeningConfigApiService,
    (screeningConfigUuid: string, companyUuid: string) => Observable<ScreeningAuditLogReportMetaCompositeEntity[]>
  >;
  postScreeningConfig(payload: ScreeningConfigPartialEntity): Observable<ScreeningConfigEntity>;
  patchScreeningConfig(
    screeningConfigUuid: string,
    payload: ScreeningConfigPartialEntity,
  ): Observable<ScreeningConfigEntity>;
  postScreeningConfigWatchlist(
    screeningConfigUuid: string,
    payload: ScreeningConfigWatchlistPartialEntity,
  ): Observable<ScreeningConfigWatchlistEntity>;
  deleteScreeningConfigWatchlist(
    screeningConfigUuid: string,
    screeningConfigWatchlistUuid: string,
  ): Observable<ScreeningConfigWatchlistEntity>;
  postScreeningConfigScreeningListCategory(
    screeningConfigUuid: string,
    payload: ScreeningConfigScreeningListCategoryPartialEntity,
  ): Observable<ScreeningConfigScreeningListCategoryEntity>;
  deleteScreeningConfigScreeningListCategory(
    screeningConfigUuid: string,
    screeningConfigScreeningListCategoryUuid: string,
  ): Observable<ScreeningConfigScreeningListCategoryEntity>;
  postScreeningConfigComposite(
    payload: ScreeningConfigPartialCompositeEntity,
  ): Observable<ScreeningConfigCompositeEntity>;
  screeningEntityCache: LocalCache<
    ScreeningConfigApiService,
    (screeningEntityUuid: string) => Observable<WithUuid<ScreeningEntityEntity>>
  >;
  screeningEntityListCache: LocalCache<
    ScreeningConfigApiService,
    (entityUuid?: string | null, customerPersonUuid?: string | null) => Observable<WithUuid<ScreeningEntityEntity>[]>
  >;
  screeningAdverseMediaCollectionCache: LocalCache<
    ScreeningConfigApiService,
    (
      screeningEntityUuid: string,
      limit?: number,
      offset?: number,
    ) => Observable<SimpleCollection<ScreeningAdverseMediaEntity>>
  >;
  postScreeningEntityEntity(payload: ScreeningEntityPostPayload): Observable<WithUuid<ScreeningEntityEntity>>;
  postScreeningActionEntity(
    screeningConfigUuid: string,
    payload: ScreeningActionPostPayload,
  ): Observable<ScreeningActionEntity>;
  patchScreeningActionEntity(
    screeningConfigUuid: string,
    payload: ScreeningActionPatchPayload,
  ): Observable<ScreeningActionEntity>;
  patchScreeningActionEntities(
    screeningConfigUuid: string,
    payload: ScreeningActionPatchPayload[],
  ): Observable<ScreeningActionEntity[]>;
  postJobScreeningAction(screeningConfigUuid: string, companyUuid?: string | null): Observable<unknown>;
  /**
   * monitoringOriginEntityUuid - can be undefined for private customer persons (not connected to a company)
   */
  createCustomerPerson(
    screeningConfigUuid: string,
    monitoringOriginEntityUuid: string | undefined,
    appMembershipUuid: string,
    entityMonitoringTypeUuid: string,
    monitoringComment: string,
    customerPersonForm: CustomerPersonForm,
  ): Observable<CustomerPersonEntity>;
  postCustomerPersonCompositeBatch(
    screeningConfigUuid: string,
    payload: CustomerPersonCompositeBatchEntity[],
  ): Observable<CustomerPersonCompositeBatchEntity>;
  updateScreeningConfigComposite(screeningConfigCompositeChanges: ScreeningConfigCompositeChanges): Observable<true>;
  postScreeningAuditLogReportMeta(
    screeningConfigUuid: string,
    payload: ScreeningAuditLogReportMetaPartialEntity,
    fileData: File,
  ): Observable<ScreeningAuditLogReportMetaCompositeEntity>;
}
export const SCREENING_CONFIG_API_SERVICE_TOKEN = new InjectionToken<ScreeningConfigApiService>(
  'ScreeningConfigApiService',
);
